import { z } from 'zod'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'
const API_TIMEOUT = 30000

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public data?: unknown
  ) {
    super(`${status} ${statusText}`)
    this.name = 'ApiError'
  }
}

export class OfflineError extends Error {
  constructor() {
    super('No internet connection')
    this.name = 'OfflineError'
  }
}

interface RequestConfig {
  headers?: HeadersInit
  signal?: AbortSignal
  timeout?: number
}

export interface ApiResponse<T> {
  data: T
  status: number
  statusText: string
}

export class BaseApi {
  private static instance: BaseApi
  private baseUrl: string
  private defaultHeaders: HeadersInit
  private tokenGetter?: (forceRefresh: boolean) => Promise<string | null>

  private constructor() {
    this.baseUrl = API_BASE_URL
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    }
  }

  public static getInstance(): BaseApi {
    if (!BaseApi.instance) {
      BaseApi.instance = new BaseApi()
    }
    return BaseApi.instance
  }

  public setTokenGetter(getter: (forceRefresh: boolean) => Promise<string | null>): void {
    this.tokenGetter = getter
  }

  private async getHeaders(config: RequestConfig = {}, forceRefresh = false): Promise<HeadersInit> {
    const headers: Record<string, string> = {
      ...this.defaultHeaders as Record<string, string>,
      ...config.headers as Record<string, string>,
    }

    if (this.tokenGetter) {
      const token = await this.tokenGetter(forceRefresh)
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
    }

    return headers
  }

  private async request<T>(
    method: string,
    endpoint: string,
    data?: unknown,
    config: RequestConfig = {},
    isRetry = false
  ): Promise<ApiResponse<T>> {
    if (!navigator.onLine) {
      throw new OfflineError()
    }

    const timeout = config.timeout ?? API_TIMEOUT
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)

    try {
      const headers = await this.getHeaders(config, isRetry)
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method,
        headers,
        body: data ? JSON.stringify(data) : undefined,
        signal: config.signal ?? controller.signal,
      })

      if (response.status === 401 && !isRetry) {
        return this.request<T>(method, endpoint, data, config, true)
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null)
        throw new ApiError(response.status, response.statusText, errorData)
      }

      const contentType = response.headers.get('content-type')
      const responseData = contentType?.includes('application/json')
        ? await response.json()
        : await response.text()

      return {
        data: responseData as T,
        status: response.status,
        statusText: response.statusText,
      }
    } finally {
      clearTimeout(timeoutId)
    }
  }

  public async get<T>(endpoint: string, config: RequestConfig = {}): Promise<ApiResponse<T>> {
    return this.request<T>('GET', endpoint, undefined, config)
  }

  public async post<T>(endpoint: string, data?: unknown, config: RequestConfig = {}): Promise<ApiResponse<T>> {
    return this.request<T>('POST', endpoint, data, config)
  }

  public async put<T>(endpoint: string, data?: unknown, config: RequestConfig = {}): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', endpoint, data, config)
  }

  public async delete<T>(endpoint: string, config: RequestConfig = {}): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', endpoint, undefined, config)
  }
}

export const api = BaseApi.getInstance()

export async function createValidatedRequest<T>(
  schema: z.ZodType<T>,
  request: Promise<ApiResponse<unknown>>
): Promise<ApiResponse<T>> {
  const response = await request
  const validatedData = schema.parse(response.data)
  return {
    ...response,
    data: validatedData,
  }
}
